// BidBuyZoom sale fees: each company's plan "% of sales" and "per sale" fees, one row per ended sale.
// Fees are charged automatically on the 1st of each month (one Stripe invoice per company) and
// failed charges are retried daily. From here a super admin can charge a company now or waive a fee.

import { useEffect, useState } from "react";
import { get, post } from "../../utils/apiManager.js";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (v) => (v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const monthName = (m) => {
  const [y, mo] = String(m || "").split("-").map(Number);
  if (!y || !mo) return m;
  return new Date(y, mo - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const STATUS = {
  pending: { label: "Due", color: "#b45309", bg: "#fef3c7" },
  failed: { label: "Charge failed", color: "#b91c1c", bg: "#fee2e2" },
  charged: { label: "Paid", color: "#047857", bg: "#d1fae5" },
  no_charge: { label: "No fee", color: "#475569", bg: "#f1f5f9" },
  waived: { label: "Waived", color: "#475569", bg: "#f1f5f9" },
};

const card = { background: "#fff", borderRadius: 14, border: "1px solid #edf0f7", boxShadow: "0 1px 4px rgba(26,39,68,.05)", marginBottom: 24 };
const th = { padding: "10px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9aa5c0", textAlign: "left", borderBottom: "1px solid #edf0f7", background: "#f8f9fc", whiteSpace: "nowrap" };
const td = { padding: "11px 14px", fontSize: 14, color: "#3a4a6a", borderBottom: "1px solid #f0f2f8", verticalAlign: "middle" };
const btn = { border: "1px solid #d5dbe8", background: "#fff", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" };

const SaleFees = () => {
  const [month, setMonth] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (month) q.set("month", month);
    if (status) q.set("status", status);
    get(`/company/sale-fees?${q.toString()}`)
      .then((res) => setData(res?.status ? res.data : null))
      .finally(() => setLoading(false));
  };

  useEffect(load, [month, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const chargeNow = async (companyId, companyName) => {
    if (!window.confirm(`Charge ${companyName}'s card now for all of its sale fees that are due, including this month's?`)) return;
    setBusy(`c-${companyId}`);
    const res = await post("/company/sale-fees/charge-now", { companyId });
    setNotice({ ok: !!res?.status, text: res?.message || "Done." });
    setBusy("");
    load();
  };

  const waive = async (fee) => {
    if (!window.confirm(`Waive the ${money(fee.totalFee)} fee for "${fee.saleTitle || "this sale"}"? It will not be charged.`)) return;
    setBusy(`w-${fee._id}`);
    const res = await post("/company/sale-fees/waive", { id: fee._id });
    setNotice({ ok: !!res?.status, text: res?.message || "Done." });
    setBusy("");
    load();
  };

  const totals = data?.totals || {};
  const records = data?.records || [];
  const boxes = [
    { key: "pending", title: "Due next charge" },
    { key: "failed", title: "Charge failed" },
    { key: "charged", title: "Paid" },
    { key: "waived", title: "Waived" },
  ];

  return (
    <div style={{ padding: "24px 0" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0a1628", margin: 0 }}>Sale Fees</h3>
          <p style={{ fontSize: 13, color: "#8a94ad", margin: "4px 0 0" }}>
            Each plan's % of the hammer total (lots that sold) plus its per-sale fee, for every sale that ends.
            Charged automatically on the 1st of each month; failed charges retry daily (5 tries). Sales that end during a free trial are not charged.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select className="form-control" style={{ width: 180 }} value={month} onChange={(e) => setMonth(e.target.value)}>
            <option value="">All months</option>
            {(data?.months || []).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
          </select>
          <select className="form-control" style={{ width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {notice && (
        <div className={`alert ${notice.ok ? "alert-success" : "alert-danger"}`} role="alert" onClick={() => setNotice(null)} style={{ cursor: "pointer" }}>
          {notice.text}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
        {boxes.map((b) => (
          <div key={b.key} style={{ ...card, marginBottom: 0, padding: "16px 18px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#9aa5c0", textTransform: "uppercase", letterSpacing: "0.06em" }}>{b.title}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: STATUS[b.key].color, marginTop: 6 }}>{money(totals[b.key]?.amount)}</div>
            <div style={{ fontSize: 12, color: "#8a94ad" }}>{totals[b.key]?.count || 0} sale(s)</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Company</th>
                <th style={th}>Sale</th>
                <th style={th}>Ended</th>
                <th style={{ ...th, textAlign: "right" }}>Hammer</th>
                <th style={th}>Rate</th>
                <th style={{ ...th, textAlign: "right" }}>Fee</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={td} colSpan={8}>Loading…</td></tr>
              ) : records.length === 0 ? (
                <tr><td style={td} colSpan={8}>No sale fees yet.</td></tr>
              ) : records.map((fee) => {
                const st = STATUS[fee.status] || STATUS.pending;
                const open = fee.status === "pending" || fee.status === "failed";
                return (
                  <tr key={fee._id}>
                    <td style={td}>{fee.companyName}</td>
                    <td style={td}>{fee.saleTitle || "—"}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{date(fee.endedAt)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{money(fee.hammerTotal)}</td>
                    <td style={{ ...td, whiteSpace: "nowrap", fontSize: 13 }}>
                      {[fee.percentRate > 0 ? `${fee.percentRate}%` : null, fee.flatFee > 0 ? `${money(fee.flatFee)}/sale` : null].filter(Boolean).join(" + ")}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{money(fee.totalFee)}</td>
                    <td style={td}>
                      <span style={{ background: st.bg, color: st.color, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                        {fee.status === "charged" ? `Paid ${date(fee.chargedAt)}` : fee.status === "waived" && fee.waivedBy === "Free trial" ? "Free trial" : st.label}
                      </span>
                      {fee.status === "failed" && fee.lastError && (
                        <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>{fee.lastError} (try {fee.attempts} of 5)</div>
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      {open && (
                        <>
                          <button style={btn} disabled={!!busy} onClick={() => chargeNow(fee.companyId, fee.companyName)}>
                            {busy === `c-${fee.companyId}` ? "Charging…" : "Charge now"}
                          </button>{" "}
                          {!fee.stripeInvoiceId && (
                            <button style={{ ...btn, color: "#b91c1c" }} disabled={!!busy} onClick={() => waive(fee)}>
                              {busy === `w-${fee._id}` ? "…" : "Waive"}
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SaleFees;
